import { SpecificRulesProduct } from '@teinor/erp/prices/types'
import { useCallback, useEffect, useState } from 'react'
import { twinFetchPostJSON } from '../../globals/data'
import { twinPreparePrice } from '../../globals/price'
import { getSeparatorsOfFormatedNumber } from '../../globals/numbers'
import { RowData } from '../../../baseComponents/TwinTable/types'
import { changeSuccessMessage } from '../../reducers/reduxDispatch'
import useTwinTranslation from '../useTwinTranslation'

export interface SpecificPricesProductLogicProps {
    ProductId: number
}

export type SpecificRulesProductExtended = SpecificRulesProduct & {
    _old_price?: number
}
const useSpecificPricesProduct = ({ ProductId }: SpecificPricesProductLogicProps) => {
    const [data, setData] = useState<SpecificRulesProductExtended[] | null>(null)
    const { t } = useTwinTranslation()
    const message = t('successfullyCompleted', 'Realizado correctamente')

    const getData = useCallback(async () => {
        if (ProductId) {
            let productPrices = await twinFetchPostJSON('/api/app/product/specificPrice/getSpecificPrices', {
                ProductId
            })
            if (productPrices) {
                productPrices = twinPreparePrice(productPrices)
                for (const productPrice of productPrices) {
                    productPrice['_old_price'] = productPrice.calculated_price
                }
            }
            setData(productPrices)
        }
    }, [ProductId])

    const saveData = useCallback(async (data: SpecificRulesProduct[] | null, MyProductId?: string) => {
        if (MyProductId || ProductId) {
            const petition = await twinFetchPostJSON('/api/app/product/specificPrice/updateSpecificPrices', {
                ProductId: ProductId || MyProductId,
                specificPriceRules: data
            })
            if (petition) {
                changeSuccessMessage(message)
                getData()
            }
        }
    }, [ProductId, getData, message])


    const changePrice = useCallback((price: string | null, rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const { decimalSeparator } = getSeparatorsOfFormatedNumber()
            let newValue = ''
            if (price) {
                newValue = price.replace(decimalSeparator, '.')
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            newDataCopy[rowIndex] = {
                ...newDataCopy[rowIndex],
                'price': price === null ? null : newValue
            }
            return twinPreparePrice([...newDataCopy])
        })
    }, [setData])

    const changeIsPack = useCallback((rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            newDataCopy[rowIndex] = {
                ...newDataCopy[rowIndex],
                'is_pack': !newDataCopy[rowIndex].is_pack
            }
            return [...newDataCopy]
        })
    }, [setData])

    const changeUnits = useCallback((units: string, rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            if (!newDataCopy[rowIndex].old_units) {
                newDataCopy[rowIndex].old_units = newDataCopy[rowIndex].units || 1
            }
            newDataCopy[rowIndex] = { ...newDataCopy[rowIndex], 'units': units }
            return twinPreparePrice([...newDataCopy])
        })
    }, [setData])

    const deleteUnits = useCallback((rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            const myData = newDataCopy[rowIndex]
            newDataCopy[rowIndex] = { ...myData, deleted: !myData.deleted }
            return twinPreparePrice([...newDataCopy])
        })
    }, [setData])

    const addUnits = useCallback((rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            const myData = newDataCopy[rowIndex]
            let last = rowIndex
            for (const key in newDataCopy) {
                const element = newDataCopy[key]
                const units = element.units || 1
                if (element.tag.includes(myData.tag)) {
                    if (newDataCopy[last].units < units) {
                        last = parseInt(key)
                    }
                }
            }
            newDataCopy.splice(last + 1, 0, { ...myData, units: (parseFloat(newDataCopy[last].units) || 1) + 1, old_units: -1 })
            return twinPreparePrice([...newDataCopy])
        })
    }, [setData])

    const syncWithParent = useCallback(async (allRowData: RowData) => {
        changePrice(null, allRowData.rowIndex)
    }, [changePrice])


    useEffect(() => {
        if (data === null) {
            getData()
        }
    }, [getData, data])

    return { data, setData, changePrice, saveData, syncWithParent, changeIsPack, changeUnits, deleteUnits, addUnits }
}

export default useSpecificPricesProduct